import React from "react";
import { Link, NavLink } from "react-router-dom";
import { ArrowUpRight, Heart, LogOut, Menu, Phone, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BeloLogo } from "@/components/BeloLogo";
import { roleHome } from "@/lib/format";
import { useBrandConfig } from "@/hooks/useBrandConfig";

const linkClass = ({ isActive }) =>
  `text-sm font-medium tracking-wide transition-colors ${
    isActive ? "text-brand" : "text-slate-600 hover:text-brand"
  }`;

// Belo Rentals — a sister site (car rentals) surfaced as a top-nav link in
// Phase 9. Opens in a new tab because the user is leaving Belomotors entirely.
const RENTALS_HREF = "https://belorentals.com";

export const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { bdc_phone_display, bdc_phone_href } = useBrandConfig();

  return (
    <header
      className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Brand */}
        <Link
          to="/"
          className="flex items-center gap-2 group transition-opacity hover:opacity-80"
          data-testid="brand-link"
          aria-label="BELO — Belomotors home"
        >
          <BeloLogo height={44} color="#0f172a" />
        </Link>

        {/* Desktop nav — Rentals sits between Dealerships and Portal because
            it's a discovery/utility link, not a first-class marketplace tab. */}
        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/inventory" className={linkClass} data-testid="nav-inventory-link">
            Inventory
          </NavLink>
          <NavLink to="/dealers" className={linkClass} data-testid="nav-dealers-link">
            Dealerships
          </NavLink>
          <a
            href={RENTALS_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium tracking-wide text-slate-600 hover:text-brand transition-colors inline-flex items-center gap-1"
            data-testid="nav-rentals-link"
            aria-label="Belo Rentals (opens in new tab)"
          >
            Rentals <ArrowUpRight size={13} className="opacity-70" />
          </a>
          {isAuthenticated && (user?.role === "dealer" || user?.role === "bdc" || user?.role === "admin") && (
            <NavLink to={roleHome(user.role)} className={linkClass} data-testid="nav-portal-link">
              Portal
            </NavLink>
          )}
        </nav>

        {/* Right cluster — BDC phone anchors *immediately* before the auth
            buttons per Phase 9 spec. Kept as a gold-tinted pill so shoppers
            spot it without eclipsing "Log in"/"Create account". */}
        <div className="flex items-center gap-2">
          <a
            href={bdc_phone_href}
            className="hidden lg:inline-flex items-center gap-1.5 rounded-full border border-[#F1E1A8] bg-[#FCF3D9] hover:bg-[#F8E9BB] px-3 py-1.5 text-xs font-semibold text-[#7A5C0E] transition-colors"
            data-testid="header-bdc-phone-link"
            aria-label={`Call BDC concierge ${bdc_phone_display}`}
          >
            <Phone size={12} className="text-[#B58B14]" />
            {bdc_phone_display}
          </a>
          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="rounded-full h-10 px-3 gap-2 border border-slate-200"
                  data-testid="user-menu-trigger"
                >
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt=""
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-light text-brand text-xs font-semibold">
                      {(user?.full_name || user?.email || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="hidden sm:inline text-sm font-medium max-w-[140px] truncate">
                    {user?.full_name || user?.email}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="user-menu-content">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col">
                    <span className="font-semibold text-slate-900">{user?.full_name}</span>
                    <span className="text-xs text-slate-500 truncate">{user?.email}</span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 mt-1">
                      {user?.role}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user?.role === "customer" && (
                  <>
                    <DropdownMenuItem asChild data-testid="menu-account-link">
                      <Link to="/account" className="cursor-pointer">
                        <User size={16} /> Account
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild data-testid="menu-saved-link">
                      <Link to="/account/saved" className="cursor-pointer">
                        <Heart size={16} /> Saved vehicles
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
                {user?.role !== "customer" && (
                  <DropdownMenuItem asChild data-testid="menu-portal-link">
                    <Link to={roleHome(user?.role)} className="cursor-pointer">
                      <User size={16} /> Portal
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-rose-600 focus:text-rose-700"
                  data-testid="menu-logout-btn"
                >
                  <LogOut size={16} /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                asChild
                variant="ghost"
                className="text-slate-700 hover:text-brand hidden sm:inline-flex"
                data-testid="header-login-btn"
              >
                <Link to="/login">Log in</Link>
              </Button>
              <Button
                asChild
                className="bg-brand hover:bg-brand-hover text-white"
                data-testid="header-signup-btn"
              >
                <Link to="/signup">Create account</Link>
              </Button>
            </>
          )}

          {/* Mobile nav */}
          <div className="md:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                  <Menu size={22} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/inventory">Inventory</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/dealers">Dealerships</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href={RENTALS_HREF} target="_blank" rel="noopener noreferrer">
                    Rentals ↗
                  </a>
                </DropdownMenuItem>
                {!isAuthenticated && (
                  <DropdownMenuItem asChild>
                    <Link to="/login">Log in</Link>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};
